import React from "react";
import classNames from "classnames";

/**
 * Proportional bar + numeric value. Width floors at 6% so a non-zero value
 * stays visible against the busiest event in view; zero renders as an
 * em-dash with no fill instead of a hairline bar.
 *
 * @param {Object}          props
 * @param {number}          props.value
 * @param {number}          props.max
 * @param {"imp"|"views"}   props.color
 */
export function MiniBar({ value, max, color }) {
  const isZero = !value;
  const pct = isZero ? 0 : Math.max(6, Math.round((value / max) * 100));
  return (
    <div className="eew-metric">
      <div className="eew-metric-track">
        {!isZero && (
          <div
            className={classNames("eew-metric-fill", `eew-metric-fill--${color}`)}
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
      <span
        className={classNames("eew-metric-value", {
          "eew-metric-value--zero": isZero,
        })}
      >
        {isZero ? "—" : value.toLocaleString()}
      </span>
    </div>
  );
}

/**
 * Card footer: bar-scale/behaviour note (tab-specific, passed as children)
 * plus the shared Impressions/Views colour legend.
 *
 * @param {Object} props
 * @param {ReactNode} props.children
 */
export function EngagementFooter({ children }) {
  return (
    <div className="eew-footer">
      <span>{children}</span>
      <div className="eew-footer-legend">
        <span className="eew-footer-legend-item">
          <span className="eew-legend-dot eew-legend-dot--imp" />
          Impressions
        </span>
        <span className="eew-footer-legend-item">
          <span className="eew-legend-dot eew-legend-dot--views" />
          Views
        </span>
      </div>
    </div>
  );
}

const SOURCE_CONFIG = [
  { key: "widget", label: "Widget", color: "var(--bg-brand-solid)" },
  { key: "direct", label: "Direct", color: "var(--fg-quaternary)" },
  { key: "qr", label: "QR code", color: "#1D9E75" },
  { key: "email", label: "Email", color: "#D85A30" },
  { key: "kiosk", label: "Kiosk", color: "#371408" },
  { key: "rss", label: "RSS Feed", color: "#30c4d8" },
];

/**
 * Expanded source breakdown row content.
 *
 * @param {Object}      props
 * @param {EventSource} props.sources
 */
export function SourceBreakdown({ sources }) {
  const chips = SOURCE_CONFIG.filter((s) => sources[s.key] > 0);

  return (
    <div className="eew-expand-inner">
      <div className="eew-expand-label">Views by source</div>
      <div className="eew-source-grid">
        {chips.map((s) => (
          <div key={s.key} className="eew-source-chip">
            <span className="eew-source-dot" style={{ background: s.color }} />
            <span className="eew-source-label">{s.label}</span>
            <span className="eew-source-count">{sources[s.key]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default MiniBar;
